import React from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { Divider } from 'react-native-paper'
import { Text, View, StyleSheet, StyleProp, ViewStyle } from 'react-native'
import { EInvoiceAddressType } from '@linqpal/models'
import capitalize from 'lodash/capitalize'
import { dateMask } from '../../../../utils/helpers/masking'
import { TooltipIconModal } from '../../../../ui/atoms/TooltipIconModal'

interface OtherInvoiceInfoProps {
  type: string
  invoice_number: string
  invoice_due_date: string
  style?: StyleProp<ViewStyle>
  dividerStyle?: StyleProp<ViewStyle>
  addressType?: EInvoiceAddressType
  address?: string
  attention?: string
  invoice_document?: any
}

export const OtherInvoiceInfo_v2: React.FC<OtherInvoiceInfoProps> = ({
  type,
  invoice_number,
  invoice_due_date,
  style,
  addressType,
  address,
  attention,
  invoice_document,
}) => {
  return (
    <>
      <View style={[styles.container, style]}>
        {/* Invoice Type and Number */}
        <View style={styles.infoRow} testID="invoice-info">
          <Text style={styles.labelText} testID="invoice-type-and_number">
            {`${capitalize(type).replace('_', ' ')} #${invoice_number}`}
          </Text>
          <Text style={styles.valueText} testID="invoice-due-date">
            {`Due on ${dateMask(invoice_due_date)}`}
          </Text>
        </View>

        <Divider style={styles.divider} testID="divider" />

        <AddressSection
          addressType={addressType}
          address={address}
          attention={attention}
          invoice_document={invoice_document}
        />
      </View>
    </>
  )
}

const AddressSection: React.FC<{
  addressType?: EInvoiceAddressType
  address?: string
  attention?: string
  invoice_document?: any
}> = ({ addressType, address, attention, invoice_document }) => {
  const { t } = useTranslation('global')

  if (!addressType || addressType !== EInvoiceAddressType.DELIVERY) return null

  const showTooltip = !!invoice_document

  return (
    <>
      <View style={styles.infoRow}>
        <View style={styles.labelWithTooltip}>
          <Text style={styles.labelText}>{t('InvoiceDetails.deliver-to')}</Text>
          {showTooltip && (
            <TooltipIconModal
              tooltip={
                <Trans
                  t={t}
                  i18nKey="InvoiceDetails.deliver-tooltip"
                  components={{
                    bold: <Text style={{ fontWeight: '700' }} />,
                  }}
                />
              }
              // eslint-disable-next-line i18next/no-literal-string
              iconName="help-circle-outline"
              containerStyle={styles.tooltipContainer}
              size={19}
              tooltipWidth={275}
            />
          )}
        </View>
        {address && <Text style={styles.valueText}>{address}</Text>}
      </View>

      {attention && (
        <>
          <Divider style={styles.divider} />
          <View style={styles.infoRow}>
            <Text style={styles.labelText}>
              {t('InvoiceDetails.attention-to')}
            </Text>
            <Text style={styles.valueText}>{attention}</Text>
          </View>
        </>
      )}

      <Divider style={styles.divider} />
    </>
  )
}

const styles = StyleSheet.create({
  container: {
    marginTop: 14,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  labelText: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: '500',
    lineHeight: 21,
    color: '#99ADBA',
    maxWidth: '50%',
  },
  valueText: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: '500',
    lineHeight: 21,
    color: '#001929',
    textAlign: 'right',
    maxWidth: '50%',
  },
  labelWithTooltip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    width: '50%',
  },
  tooltipContainer: {
    alignItems: 'center',
    marginTop: 3,
  },
  divider: {
    marginTop: 14,
    marginBottom: 14,
  },
})
